/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Oct 19 2023
 **/

import { useCallback } from "react";

import { useSpaceChannelState } from "@/frameworks/core/chat";
import { useFullSizeChatManager, useMinimizeChatList } from "@/frameworks/core/hooks";
import { Button, Column, Row, TabContainer } from "@/frameworks/designStuff/components";
import { Typography } from "@/frameworks/designStuff/components";
import { EventHandler } from "@rfice/eventhandler";
import { EventID } from "@/frameworks/core/commons";
import { _T } from "@rfice/language";

import FullSizeChatContent from "./FullSizeSpaceChatContent";
import FullSizeOpenWorldChatContent from "./FullSizeOpenWorldChatContent";

const PADDING_OFFSET = 40;
const ELLIPSIS_OFFSET = 32;

export default function FullSizeSpaceChat({
	roomID,
	spaceID,
	handleChangeSpace,
	activityMessageID
}: {
	roomID: string;
	spaceID: string;
	handleChangeSpace: (id: string) => void;
	activityMessageID?: string;
}) {
	const { minimizeChatListActions } = useMinimizeChatList();
	const { fullSizeChatActions } = useFullSizeChatManager();
	const spaceInfo = useSpaceChannelState(spaceID);
	const isOpenWorld = (spaceInfo?.spaceType ?? "NORMAL") === "OPEN_WORLD";

	const handleClickIcon = (ev: React.MouseEvent<HTMLButtonElement>) => {
		const target = ev.currentTarget as HTMLButtonElement;

		switch (target.id) {
			case "close": {
				fullSizeChatActions.hide();
				minimizeChatListActions.remove(roomID);
				break;
			}
		}
	};

	const toggleOpenSearchChatInput = useCallback(() => {
		EventHandler.emit(EventID.TOGGLE_OPEN_SEARCH_ON_CONVERSATION_INPUT, { roomId: roomID });
	}, [roomID]);

	return (
		<Column css={{ width: "100%", height: "100%" }}>
			<Row
				horizonAlign="distribute"
				verticalAlign="center"
				css={{ width: "100%", padding: "10px 20px", height: "56px" }}
			>
				<Button.Icon id="close" name="Left24" size="large" filled="transparent" onClick={handleClickIcon} />
				<Typography
					maxLine={1}
					typoSize="Head6"
					style={{ width: `calc(100% - ${PADDING_OFFSET}px - ${ELLIPSIS_OFFSET}px)`, display: "block" }}
				>
					{isOpenWorld ? _T("오픈월드") : spaceInfo?.spaceName ?? _T("마이룸")}
				</Typography>
				<Row gap={12}>
					<Button.Icon name="Search24" size="large" variant="text" onClick={toggleOpenSearchChatInput} />

					<Button.Icon
						id="close"
						name="Close24"
						size="large"
						filled="transparent"
						onClick={handleClickIcon}
					/>
				</Row>
			</Row>
			{isOpenWorld ? (
				<TabContainer activeId={spaceID}>
					<FullSizeOpenWorldChatContent />
				</TabContainer>
			) : (
				<TabContainer activeId={roomID}>
					<FullSizeChatContent
						spaceID={spaceID}
						roomID={roomID}
						onChangeSpace={handleChangeSpace}
						activityMessageID={activityMessageID}
					/>
				</TabContainer>
			)}
		</Column>
	);
}
